// src/components/PatientsReportScreen.jsx
// Relatório de pacientes premium.
// Rota: /reports/patients
// Endpoint: GET /psy/report/patients?id=ID_USUARIO
// UI isolada em .pat-* para não mexer em .dash-* global.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AlertCircle,
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  Info,
  MapPin,
  Menu,
  Printer,
  RefreshCw,
  Search,
  UserCheck,
  UserPlus,
  Users,
  Video,
  X,
} from "lucide-react";

import AppSidebar from "./AppSidebar";
import Logo from "../assets/logoClaro.webp";
import "../sass/pages/patients-report.scss";
import { API_BASE as CONFIG_API_BASE } from "../config/apiBase";

const API_BASE = (
  CONFIG_API_BASE ||
  process.env.REACT_APP_API_URL ||
  "https://api.psyassist.com.br"
).replace(/\/$/, "");

const AUTH_KEYS = {
  TOKEN: "psy_token",
  USER_ID: "psy_id",
  USER_NAME: "psy_usuario",
};

const PATIENT_FILTERS = [
  { value: "todos", label: "Todos" },
  { value: "online", label: "Online" },
  { value: "presencial", label: "Presencial" },
];

const ACTIVE_FILTERS = [
  { value: "todos", label: "Todos" },
  { value: "true", label: "Ativos" },
  { value: "false", label: "Inativos" },
];

const YES_NO_FILTERS = [
  { value: "todos", label: "Todos" },
  { value: "true", label: "Sim" },
  { value: "false", label: "Não" },
];

function readStorage(keys = []) {
  for (const key of keys) {
    const value = sessionStorage.getItem(key) || localStorage.getItem(key);

    if (value && String(value).trim() !== "") {
      return value;
    }
  }

  return "";
}

function saveAuthToken(token) {
  if (!token) return;

  localStorage.setItem(AUTH_KEYS.TOKEN, token);
  sessionStorage.setItem(AUTH_KEYS.TOKEN, token);
}

function normalizeText(value = "") {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

function safeNumber(value, fallback = 0) {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
}

function percentBR(value) {
  return new Intl.NumberFormat("pt-BR", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(safeNumber(value));
}

function buildQuery(params = {}) {
  const query = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null) return;
    if (String(value).trim() === "") return;
    if (String(value).trim() === "todos") return;
    query.set(key, String(value));
  });

  return query.toString();
}

function parseDate(value) {
  if (!value) return null;

  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value;
  }

  const date = new Date(value);

  return Number.isNaN(date.getTime()) ? null : date;
}

function formatDateBR(value) {
  const date = parseDate(value);

  if (!date) return "Não registrado";

  return new Intl.DateTimeFormat("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(date);
}

function getTimeBR(value) {
  const date = parseDate(value);

  if (!date) return "--:--";

  return new Intl.DateTimeFormat("pt-BR", {
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function getFirstName(name = "") {
  const clean = String(name || "").trim();

  if (!clean) return "";

  return clean.split(/\s+/)[0];
}

function getInitials(name = "") {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  if (!parts.length) return "P";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();

  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

function getPatientType(raw = "") {
  const normalized = normalizeText(raw);

  if (normalized.includes("online")) return "online";
  if (normalized.includes("pres")) return "presencial";

  return normalized || "presencial";
}

function getPatientTypeLabel(raw = "") {
  const type = getPatientType(raw);

  if (type === "online") return "Online";
  return "Presencial";
}

function getPatientTypeIcon(raw = "") {
  const type = getPatientType(raw);

  if (type === "online") {
    return <Video size={14} aria-hidden="true" />;
  }

  return <MapPin size={14} aria-hidden="true" />;
}

function normalizePatients(data = []) {
  const rows = Array.isArray(data) ? data : [];

  return rows.slice(0, 80).map((item, index) => {
    const nome = String(item?.nome || item?.name || item?.cliente || "Paciente").trim();
    const tipo = getPatientTypeLabel(item?.tipo || item?.tipoAtendimento || item?.modalidade || item?.tipoUltimaSessao || "");
    const desde = item?.desde || item?.createdAt || item?.dataCadastro || item?.ultimoAtendimento || item?.ultimaSessao || null;

    return {
      id: item?._id || item?.id || `${nome}-${tipo}-${index}`,
      nome,
      tipo,
      tipoKey: getPatientType(tipo),
      desde,
      totalSessoes: safeNumber(item?.totalSessoes ?? item?.sessoes ?? item?.totalAtendimentos ?? 0),
      ultimoAtendimento: item?.ultimoAtendimento || item?.ultimaSessao || null,
      dataInicioTerapia: item?.dataInicioTerapia || null,
      qtdContatosEmergencia: safeNumber(item?.qtdContatosEmergencia ?? item?.contatosEmergencia?.length ?? 0),
      ativo: item?.ativo !== false,
      status: String(item?.status || item?.situacao || (item?.ativo === false ? "Inativo" : "Ativo")).trim(),
    };
  });
}

function buildPatientSearchText(patient) {
  return normalizeText(
    [
      patient.nome,
      patient.tipo,
      patient.status,
      formatDateBR(patient.desde),
      formatDateBR(patient.ultimoAtendimento),
      String(patient.totalSessoes),
    ]
      .filter(Boolean)
      .join(" ")
  );
}

async function readApiError(response) {
  try {
    const contentType = response.headers.get("content-type") || "";

    if (contentType.includes("application/json")) {
      const payload = await response.json();
      return payload?.message || payload?.error || JSON.stringify(payload);
    }

    const text = await response.text();
    return text || `HTTP ${response.status}`;
  } catch {
    return `HTTP ${response.status}`;
  }
}

function getPatientHealth({ total, ativos, novos }) {
  const totalNumber = safeNumber(total);
  const activeNumber = safeNumber(ativos);
  const newNumber = safeNumber(novos);
  const activeRate = totalNumber > 0 ? (activeNumber / totalNumber) * 100 : 0;

  if (totalNumber <= 0) {
    return {
      label: "Sem pacientes",
      tone: "neutral",
      icon: "i",
      description: "Ainda não há dados suficientes para leitura da carteira.",
      activeRate,
    };
  }

  if (activeRate >= 70) {
    return {
      label: "Carteira saudável",
      tone: "good",
      icon: "good",
      description: "A maior parte da base está ativa no período.",
      activeRate,
    };
  }

  if (newNumber > 0 && activeRate >= 45) {
    return {
      label: "Em crescimento",
      tone: "warning",
      icon: "growth",
      description: "Há novos pacientes e uma base ativa relevante.",
      activeRate,
    };
  }

  return {
    label: "Acompanhar",
    tone: "warning",
    icon: "!",
    description: "Vale acompanhar reativações e frequência de atendimento.",
    activeRate,
  };
}

function HealthStatusIcon({ tone }) {
  const Icon =
    tone === "good"
      ? CheckCircle2
      : tone === "bad"
        ? AlertCircle
        : tone === "warning"
          ? AlertTriangle
          : Info;

  return <Icon size={22} aria-hidden="true" />;
}

function KpiCard({
  icon: Icon,
  label,
  value,
  description,
  tone = "blue",
}) {
  return (
    <article className={`pat-kpi pat-kpi--${tone}`}>
      <div className="pat-kpi__icon" aria-hidden="true">
        <Icon size={19} />
      </div>

      <div>
        <span>{label}</span>
        <strong>{value}</strong>
        <small>{description}</small>
      </div>
    </article>
  );
}

function DonutChart({ online, presencial }) {
  const total = safeNumber(online) + safeNumber(presencial);
  const onlinePercent = total > 0 ? (safeNumber(online) / total) * 100 : 0;
  const presencialPercent = total > 0 ? (safeNumber(presencial) / total) * 100 : 0;

  return (
    <div className="pat-pie">
      <div className="pat-donut" role="img" aria-label="Distribuição de pacientes">
        <div
          className="pat-donut__ring"
          style={{
            background: total
              ? `conic-gradient(var(--pat-blue-500) 0 ${onlinePercent}%, var(--pat-green-500) ${onlinePercent}% 100%)`
              : "conic-gradient(var(--pat-slate-200) 0 100%)",
          }}
        />

        <div className="pat-donut__hole">
          <strong>{total}</strong>
          <span>Online + Presencial</span>
        </div>
      </div>

      <div className="pat-legend">
        <LegendRow
          color="blue"
          label="Online"
          value={online}
          percent={`${percentBR(onlinePercent)}%`}
        />

        <LegendRow
          color="green"
          label="Presencial"
          value={presencial}
          percent={`${percentBR(presencialPercent)}%`}
        />
      </div>
    </div>
  );
}

function LegendRow({ color, label, value, percent }) {
  return (
    <div className="pat-legend__row">
      <span className={`pat-dot pat-dot--${color}`} aria-hidden="true" />
      <div>
        <strong>{label}</strong>
        <small>{percent}</small>
      </div>
      <span>{value}</span>
    </div>
  );
}

function PatientsSkeleton() {
  return (
    <div className="pat-skeleton" aria-hidden="true">
      <div className="pat-skeleton__hero" />
      <div className="pat-skeleton__kpis">
        <span />
        <span />
        <span />
      </div>
      <div className="pat-skeleton__panel" />
      <div className="pat-skeleton__list">
        <span />
        <span />
        <span />
      </div>
    </div>
  );
}

function EmptyState({
  title,
  message,
  onRetry,
  onBack,
  isError = false,
}) {
  return (
    <section
      className={`pat-state ${isError ? "pat-state--error" : ""}`}
    >
      <div className="pat-state__icon" aria-hidden="true">
        {isError ? (
          <AlertCircle size={24} />
        ) : (
          <Users size={24} />
        )}
      </div>

      <strong>{title}</strong>
      <span>{message}</span>

      <div className="pat-state__actions">
        <button
          className="pat-btn pat-btn--ghost-dark"
          type="button"
          onClick={onBack}
        >
          <ArrowLeft size={16} />
          Indicadores
        </button>

        <button
          className="pat-btn pat-btn--primary"
          type="button"
          onClick={onRetry}
        >
          <RefreshCw size={16} />
          Tentar novamente
        </button>
      </div>
    </section>
  );
}

export default function PatientsReportScreen() {
  const navigate = useNavigate();

  const [sidebarOpen, setSidebarOpen] = useState(false);

  const [report, setReport] = useState(null);
  const [ativos, setAtivos] = useState(0);
  const [novos, setNovos] = useState(0);
  const [total, setTotal] = useState(0);
  const [online, setOnline] = useState(0);
  const [presencial, setPresencial] = useState(0);
  const [recentes, setRecentes] = useState([]);

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);

  const [filter, setFilter] = useState("todos");
  const [search, setSearch] = useState("");

  const [ativoFilter, setAtivoFilter] = useState("todos");
  const [comSessaoFilter, setComSessaoFilter] = useState("todos");
  const [comContatoFilter, setComContatoFilter] = useState("todos");
  const [terapiaInicio, setTerapiaInicio] = useState("");
  const [terapiaFim, setTerapiaFim] = useState("");

  const nomeUsuario = useMemo(() => readStorage([AUTH_KEYS.USER_NAME]) || "", []);
  const firstName = useMemo(() => getFirstName(nomeUsuario), [nomeUsuario]);

  const authOrRedirect = useCallback(() => {
    const token = readStorage([AUTH_KEYS.TOKEN, "token"]);
    const userId = readStorage([AUTH_KEYS.USER_ID, "id"]);

    if (!token || !userId) {
      setError("Sessão expirada. Faça login novamente.");
      return null;
    }

    return { token, userId };
  }, []);

  const loadReport = useCallback(
    async ({ signal, silent = false } = {}) => {
      const auth = authOrRedirect();

      if (!auth) {
        setLoading(false);
        return;
      }

      if (silent) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      setError("");

      try {
        const params = buildQuery({
          id: auth.userId,
          ativo: ativoFilter,
          comSessao: comSessaoFilter,
          comContatoEmergencia: comContatoFilter,
          dataInicioTerapiaInicio: terapiaInicio,
          dataInicioTerapiaFim: terapiaFim,
        });

        const response = await fetch(
          `${API_BASE}/psy/report/patients?${params}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${auth.token}`,
              "Content-Type": "application/json",
            },
            signal,
          }
        );

        saveAuthToken(response.headers.get("x-new-token"));

        if (response.status === 401 || response.status === 403) {
          setError("Sessão expirada. Faça login novamente.");
          setReport(null);
          return;
        }

        if (!response.ok) {
          const message = await readApiError(response);
          setError(message || `Erro ao buscar relatório (${response.status}).`);
          setReport(null);
          return;
        }

        const data = await response.json().catch(() => null);

        const nextAtivos = safeNumber(data?.resumo?.ativos);
        const nextNovos = safeNumber(data?.resumo?.novos ?? data?.resumo?.novos30Dias);
        const nextTotal = safeNumber(data?.resumo?.total);
        const nextOnline = safeNumber(data?.distribuicao?.online ?? data?.distribuicao?.tipoSessao?.online);
        const nextPresencial = safeNumber(data?.distribuicao?.presencial ?? data?.distribuicao?.tipoSessao?.presencial);
        const nextRecentes = Array.isArray(data?.listaDetalhada)
          ? data.listaDetalhada
          : Array.isArray(data?.recentes)
            ? data.recentes
            : [];

        setReport(data || {});
        setAtivos(nextAtivos);
        setNovos(nextNovos);
        setTotal(nextTotal);
        setOnline(nextOnline);
        setPresencial(nextPresencial);
        setRecentes(nextRecentes);
        setLastUpdatedAt(new Date());
      } catch (err) {
        if (err?.name === "AbortError") return;

        console.error("Erro ao carregar relatório de pacientes:", err);
        setError("Falha de comunicação com a API.");
        setReport(null);
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [ativoFilter, authOrRedirect, comContatoFilter, comSessaoFilter, terapiaFim, terapiaInicio]
  );

  useEffect(() => {
    const controller = new AbortController();
    loadReport({ signal: controller.signal });

    return () => controller.abort();
  }, [loadReport]);

  const patients = useMemo(() => normalizePatients(recentes), [recentes]);

  const filteredPatients = useMemo(() => {
    const query = normalizeText(search);

    return patients.filter((patient) => {
      const matchesFilter = filter === "todos" || patient.tipoKey === filter;
      const matchesSearch = !query || buildPatientSearchText(patient).includes(query);

      return matchesFilter && matchesSearch;
    });
  }, [patients, filter, search]);

  const filteredCounter = useMemo(() => {
    return filteredPatients.reduce(
      (acc, patient) => {
        acc.total += 1;
        acc[patient.tipoKey] = safeNumber(acc[patient.tipoKey]) + 1;
        return acc;
      },
      { total: 0, online: 0, presencial: 0 }
    );
  }, [filteredPatients]);

  const pie = useMemo(() => {
    const sum = online + presencial;
    const onlinePercent = sum > 0 ? (online / sum) * 100 : 0;
    const presencialPercent = sum > 0 ? (presencial / sum) * 100 : 0;

    return {
      sum,
      onlinePercent,
      presencialPercent,
    };
  }, [online, presencial]);

  const health = useMemo(
    () => getPatientHealth({ total, ativos, novos }),
    [total, ativos, novos]
  );

  const reload = () => loadReport({ silent: true });
  const clearManagementFilters = () => {
    setAtivoFilter("todos");
    setComSessaoFilter("todos");
    setComContatoFilter("todos");
    setTerapiaInicio("");
    setTerapiaFim("");
    setFilter("todos");
    setSearch("");
  };
  const goBack = () => navigate("/reports");
  const goLogin = () => navigate("/login");
  const goPatients = () => navigate("/clients");
  const printReport = () => window.print();

  return (
    <div className="pat-page">
      <div className="pat-bg" aria-hidden="true">
        <span className="pat-bg__orb pat-bg__orb--one" />
        <span className="pat-bg__orb pat-bg__orb--two" />
        <span className="pat-bg__orb pat-bg__orb--three" />
      </div>

      <AppSidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <header className="pat-topbar">
        <button
          className="pat-icon-btn"
          type="button"
          onClick={() => setSidebarOpen(true)}
          aria-label="Abrir menu"
        >
          <Menu size={19} aria-hidden="true" />
        </button>

        <div className="pat-brand">
          <img src={Logo} alt="PsyAssist" className="pat-brand__logo" />
          <div className="pat-brand__text">
            <strong>Relatório de pacientes</strong>
            <span>Resumo, distribuição e recentes • {nomeUsuario || "Usuário"}</span>
          </div>
        </div>

        <div className="pat-top-actions">
          <button
            className="pat-btn pat-btn--ghost pat-btn--desktop"
            type="button"
            onClick={goBack}
          >
            <ArrowLeft size={16} />
            Indicadores
          </button>

          <button
            className="pat-btn pat-btn--ghost"
            type="button"
            onClick={printReport}
            disabled={loading || Boolean(error) || !report}
          >
            <Printer size={16} />
            Imprimir
          </button>

          <button
            className="pat-btn pat-btn--primary"
            type="button"
            onClick={reload}
            disabled={loading || refreshing}
          >
            <RefreshCw
              size={16}
              className={refreshing ? "report-spin" : ""}
              aria-hidden="true"
            />
            {refreshing ? "Atualizando..." : "Atualizar"}
          </button>
        </div>
      </header>

      <main className="pat-main">
        {loading ? (
          <PatientsSkeleton />
        ) : error ? (
          <EmptyState
            title="Não foi possível carregar os pacientes."
            message={error}
            onRetry={String(error).includes("Sessão") ? goLogin : reload}
            onBack={goBack}
            isError
          />
        ) : !report ? (
          <EmptyState
            title="Nenhum dado encontrado."
            message="Ainda não há pacientes suficientes para montar este relatório."
            onRetry={reload}
            onBack={goBack}
          />
        ) : (
          <>
            <section className="pat-hero">
              <div>
                <span className="pat-kicker">
                  <Users size={14} />
                  Carteira de pacientes
                </span>
                <h1>Pacientes</h1>
                <p>
                  {firstName ? `${firstName}, ` : ""}
                  acompanhe pacientes ativos, novos cadastros, distribuição por
                  modalidade e lista de pacientes recentes.
                </p>
              </div>

              <aside className={`pat-hero-card pat-hero-card--${health.tone}`}>
                <div className="pat-hero-card__icon" aria-hidden="true">
                  <HealthStatusIcon tone={health.tone} />
                </div>

                <div>
                  <small>Status da carteira</small>
                  <strong>{health.label}</strong>
                  <span>
                    Atividade: {percentBR(health.activeRate)}% • {health.description}
                  </span>
                </div>
              </aside>
            </section>

            <section className="pat-panel pat-management-filters">
              <div className="pat-panel__head">
                <div>
                  <span>Filtros de gestão</span>
                  <h2>Refine a carteira</h2>
                  <p>Use os filtros para localizar pacientes por atividade, vínculo terapêutico e contato de emergência.</p>
                </div>
              </div>

              <div className="pat-filter-grid">
                <label className="pat-filter-field">
                  <span>Situação</span>
                  <select value={ativoFilter} onChange={(event) => setAtivoFilter(event.target.value)}>
                    {ACTIVE_FILTERS.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </label>

                <label className="pat-filter-field">
                  <span>Com sessão</span>
                  <select value={comSessaoFilter} onChange={(event) => setComSessaoFilter(event.target.value)}>
                    {YES_NO_FILTERS.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </label>

                <label className="pat-filter-field">
                  <span>Contato emergência</span>
                  <select value={comContatoFilter} onChange={(event) => setComContatoFilter(event.target.value)}>
                    {YES_NO_FILTERS.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </label>

                <label className="pat-filter-field">
                  <span>Início terapia de</span>
                  <input type="date" value={terapiaInicio} onChange={(event) => setTerapiaInicio(event.target.value)} />
                </label>

                <label className="pat-filter-field">
                  <span>Início terapia até</span>
                  <input type="date" value={terapiaFim} onChange={(event) => setTerapiaFim(event.target.value)} />
                </label>

                <div className="pat-filter-actions">
                  <button className="pat-btn pat-btn--ghost-dark" type="button" onClick={clearManagementFilters}>Limpar</button>
                  <button className="pat-btn pat-btn--primary" type="button" onClick={reload} disabled={refreshing}>Aplicar</button>
                </div>
              </div>
            </section>

            <section className="pat-kpis">
              <KpiCard
                icon={UserCheck}
                label="Ativos"
                value={ativos}
                description={`${percentBR(total > 0 ? (ativos / total) * 100 : 0)}% da base`}
                tone="blue"
              />

              <KpiCard
                icon={UserPlus}
                label="Novos"
                value={novos}
                description="Entradas recentes"
                tone="green"
              />

              <KpiCard
                icon={Users}
                label="Total"
                value={total}
                description="Base consolidada"
                tone="amber"
              />
            </section>

            <section className="pat-grid2">
              <div className="pat-panel">
                <div className="pat-panel__head">
                  <div>
                    <span>Distribuição</span>
                    <h2>Online × presencial</h2>
                    <p>Proporção de pacientes por modalidade predominante.</p>
                  </div>
                </div>

                <DonutChart online={online} presencial={presencial} />
              </div>

              <div className="pat-panel">
                <div className="pat-panel__head">
                  <div>
                    <span>Resumo</span>
                    <h2>Leitura da carteira</h2>
                    <p>Resumo rápido dos indicadores principais da base.</p>
                  </div>

                  {lastUpdatedAt ? (
                    <div className="pat-updated">
                      Atualizado às {getTimeBR(lastUpdatedAt)}
                    </div>
                  ) : null}
                </div>

                <div className="pat-insight">
                  <span>Modalidade predominante</span>
                  <strong>
                    {pie.sum <= 0
                      ? "Sem dados"
                      : online >= presencial
                        ? "Online"
                        : "Presencial"}
                  </strong>
                  <p>
                    Online representa <b>{percentBR(pie.onlinePercent)}%</b> e presencial
                    representa <b> {percentBR(pie.presencialPercent)}%</b> da distribuição.
                  </p>

                  <button className="pat-btn pat-btn--ghost-dark" type="button" onClick={goPatients}>
                    Abrir pacientes
                  </button>
                </div>
              </div>
            </section>

            <section className="pat-panel">
              <div className="pat-panel__head pat-panel__head--with-tools">
                <div>
                  <span>Histórico</span>
                  <h2>Pacientes recentes</h2>
                  <p>Busque por nome, modalidade, status ou data.</p>
                </div>

                <div className="pat-counter">
                  {filteredPatients.length} de {patients.length}
                </div>
              </div>

              <div className="pat-toolbar">
                <label className="pat-search">
                  <span aria-hidden="true"><Search size={16} /></span>
                  <input
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    placeholder="Buscar paciente..."
                  />

                  {search ? (
                    <button type="button" onClick={() => setSearch("")} aria-label="Limpar busca">
                      <X size={14} />
                    </button>
                  ) : null}
                </label>

                <div className="pat-filter-group" aria-label="Filtro de pacientes">
                  {PATIENT_FILTERS.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      className={filter === option.value ? "is-active" : ""}
                      onClick={() => setFilter(option.value)}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>

                <div className="pat-total-chip">
                  <span>Filtrados</span>
                  <strong>{filteredCounter.total}</strong>
                </div>
              </div>

              {filteredPatients.length === 0 ? (
                <div className="pat-empty">
                  Nenhum paciente encontrado com os filtros atuais.
                </div>
              ) : (
                <div className="pat-list">
                  {filteredPatients.map((patient) => (
                    <article className={`pat-row is-${patient.tipoKey}`} key={patient.id}>
                      <div className={`pat-row__avatar is-${patient.tipoKey}`} aria-hidden="true">
                        {getInitials(patient.nome)}
                      </div>

                      <div className="pat-row__content">
                        <strong>{patient.nome || "Paciente"}</strong>
                        <span>
                          Desde: {formatDateBR(patient.desde)}
                          {patient.totalSessoes ? ` • ${patient.totalSessoes} sessões` : ""}
                          {patient.dataInicioTerapia ? ` • terapia: ${formatDateBR(patient.dataInicioTerapia)}` : ""}
                        </span>
                      </div>

                      <div className={`pat-row__tag is-${patient.tipoKey}`}>
                        {getPatientTypeIcon(patient.tipo)} {patient.tipo}
                      </div>
                    </article>
                  ))}
                </div>
              )}
            </section>

            <section className="pat-bottom-actions">
              <button className="pat-btn pat-btn--ghost-dark" type="button" onClick={goBack}>
                <ArrowLeft size={16} />
                Central de indicadores
              </button>

              <button className="pat-btn pat-btn--primary" type="button" onClick={goPatients}>
                <Users size={16} />
                Abrir pacientes
              </button>
            </section>
          </>
        )}
      </main>
    </div>
  );
}
